import { useRef, useState } from "react";
import { Wand2, Clock, PenLine, Play, Loader2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { ugcAPI } from "../../lib/api";
import { toast } from "sonner";

const OptionButton = ({ testId, icon: Icon, title, hint, onClick, disabled, recommended }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className={`w-full text-left p-3 rounded-xl border transition-colors flex gap-3 items-start disabled:opacity-40 disabled:cursor-not-allowed ${
      recommended ? "border-cyan-500/60 bg-cyan-500/10 hover:bg-cyan-500/20" : "border-white/10 bg-white/5 hover:bg-white/10"
    }`}
  >
    <Icon className={`w-4 h-4 mt-0.5 ${recommended ? "text-cyan-400" : "text-white/60"}`} />
    <span>
      <span className="block text-sm text-white font-medium">{title}{recommended && <span className="ml-2 text-[10px] uppercase tracking-wide text-cyan-400">Recommended</span>}</span>
      <span className="block text-xs text-white/50">{hint}</span>
    </span>
  </button>
);

export function ScriptFitPanel({ open, fit, fitRequest, onClose, onGenerate, onEdit }) {
  const [shortening, setShortening] = useState(false);
  const [shortened, setShortened] = useState(null);
  const editAfterClose = useRef(false);

  if (!fit) return null;
  const longer = fit.longer_option;
  const close = () => { setShortened(null); onClose(); };

  const shorten = async () => {
    setShortening(true);
    try {
      setShortened(await ugcAPI.fitScript(fitRequest));
    } catch {
      toast.error("Couldn't shorten the script. Try again or edit it yourself.");
    } finally {
      setShortening(false);
    }
  };

  const finish = (overrides) => { setShortened(null); onGenerate(overrides); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent
        data-testid="script-fit-panel"
        className="bg-[#111118] border-white/10 max-w-lg"
        onCloseAutoFocus={(e) => {
          if (!editAfterClose.current) return;
          e.preventDefault();
          editAfterClose.current = false;
          onEdit();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400 text-lg">
            <AlertTriangle className="w-5 h-5" /> Script is longer than the video
          </DialogTitle>
          <DialogDescription data-testid="script-fit-summary" className="text-white/70 text-sm">
            About {fit.estimated_speech_seconds}s of speech for a {fit.duration}s video ({fit.word_count} words, ~{fit.target_words} fit).
          </DialogDescription>
        </DialogHeader>

        {!shortened ? (
          <>
            <div data-testid="script-fit-preview" className="max-h-40 overflow-y-auto rounded-lg bg-black/40 p-3 text-sm leading-relaxed">
              <span className="text-white">{fit.kept_text}</span>{" "}
              <span className="text-white/30 line-through decoration-white/20">{fit.cut_text}</span>
            </div>
            <div className="space-y-2">
              <OptionButton testId="fit-option-shorten" icon={shortening ? Loader2 : Wand2} recommended disabled={shortening}
                title={shortening ? "Shortening..." : "Shorten for me"} hint="AI rewrites it to fit, keeping your hook and call-to-action" onClick={shorten} />
              {longer && (
                <OptionButton testId="fit-option-longer" icon={Clock} disabled={!longer.allowed || !longer.fits}
                  title={`Make the video ${longer.duration}s`}
                  hint={!longer.allowed ? (longer.message || "Not available on your plan") : !longer.fits ? "Still too long for 30s" : `Uses a ${longer.duration}s video from your plan`}
                  onClick={() => finish({ videoDuration: longer.duration })} />
              )}
              <OptionButton testId="fit-option-edit" icon={PenLine} title="Edit it myself" hint="The counter turns green when it fits" onClick={() => { editAfterClose.current = true; close(); }} />
              <OptionButton testId="fit-option-continue" icon={Play} title="Continue anyway" hint="Generate as is - the faded ending will be cut" onClick={() => finish({})} />
            </div>
          </>
        ) : (
          <>
            <div data-testid="script-fit-shortened" className="max-h-48 overflow-y-auto rounded-lg bg-black/40 p-3 text-sm text-white leading-relaxed">
              {shortened.script}
            </div>
            <p data-testid="script-fit-shortened-status" className={`text-xs ${shortened.fits ? "text-emerald-400" : "text-amber-400"}`}>
              {shortened.word_count} words · {shortened.fits ? `fits ${fit.duration}s` : `still a little long for ${fit.duration}s - edit it or continue`}
            </p>
            <div className="flex gap-2">
              <Button data-testid="fit-shortened-back" variant="outline" className="flex-1 border-white/20 text-white/80" onClick={() => setShortened(null)}>Back</Button>
              <Button data-testid="fit-shortened-generate" className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-black font-medium"
                onClick={() => finish({ script: shortened.script, hook: "", applyScript: true })}>Use & Generate</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
